"use client";

import { useEffect } from "react";
import * as Sentry from "@sentry/nextjs";

// DSN comes exclusively from the env var. No hardcoded fallback: if it is
// unset, the client Sentry SDK simply does not initialize.
const DSN = process.env.NEXT_PUBLIC_SENTRY_DSN;

const isProd = process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT === "production";

/**
 * Guaranteed client-side Sentry init.
 *
 * Next.js 16 + Turbopack does NOT execute `instrumentation-client.ts` in the
 * production build (the module is bundled but its init module is registered as
 * a deferred `private-next-instrumentation-client` entry that the runtime never
 * invokes — `window.__SENTRY__` and `globalThis._sentryNextJsVersion` stay
 * undefined, zero client events reach Sentry). The server side works because
 * `instrumentation.ts` `register()` runs fine.
 *
 * Rendering this client component from the root layout puts the init on the
 * real component tree, which always executes on hydration. `getClient()` guards
 * against a double init if Next ever does start running instrumentation-client.
 */
export function SentryClientInit() {
  useEffect(() => {
    if (!DSN) return;

    const existing = Sentry.getClient();
    if (existing?.getDsn()) return;

    Sentry.init({
      dsn: DSN,
      tracesSampleRate: isProd ? 0.1 : 0,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: isProd ? 0.2 : 0.1,
      profilesSampleRate: 0,
      environment: process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT ?? "production",
      release: process.env.NEXT_PUBLIC_COMMIT_SHA,
      integrations: [
        Sentry.replayIntegration({
          maskAllText: false,
          maskAllInputs: true,
          blockAllMedia: false,
        }),
      ],
    });
  }, []);

  return null;
}
